import { useEffect, useState } from 'react'
import { apiJson } from '../../lib/api'
import { schemas, type EntitySchema, type FieldSchema } from './entitySchemas'
import { BilingualField, Field, ui } from './shared'
import { MediaPicker } from './MediaPicker'

type Row = Record<string, unknown>

function renderFieldInput(f: FieldSchema, row: Row, setRow: (r: Row) => void, keySuffix = '') {
  const key = f.key + keySuffix
  const v = row[key]
  const set = (value: unknown) => setRow({ ...row, [key]: value })
  switch (f.kind) {
    case 'text':
    case 'url':
      return <input style={ui.input} value={(v as string) ?? ''} placeholder={f.placeholder} onChange={(e) => set(e.target.value)} />
    case 'textarea':
      return <textarea rows={4} style={ui.input} value={(v as string) ?? ''} onChange={(e) => set(e.target.value)} />
    case 'date':
      return <input type="date" style={ui.input} value={(v as string) ?? ''} onChange={(e) => set(e.target.value || null)} />
    case 'time':
      return <input type="time" style={ui.input} value={(v as string) ?? ''} onChange={(e) => set(e.target.value || null)} />
    case 'number':
      return <input type="number" style={ui.input} value={(v as number) ?? 0} onChange={(e) => set(Number(e.target.value))} />
    case 'image':
      return <MediaPicker value={(v as string) ?? ''} onChange={(u) => set(u)} />
    case 'bool':
      return (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input type="checkbox" checked={Boolean(v)} onChange={(e) => set(e.target.checked ? 1 : 0)} />
          <span>{f.label}</span>
        </label>
      )
  }
}

function FieldRow({ f, row, setRow }: { f: FieldSchema; row: Row; setRow: (r: Row) => void }) {
  if (f.bilingual) {
    return (
      <BilingualField
        label={f.label}
        valueIt={(row[f.key + '_it'] as string) ?? ''}
        valueEn={(row[f.key + '_en'] as string) ?? ''}
        onItChange={(v) => setRow({ ...row, [f.key + '_it']: v })}
        onEnChange={(v) => setRow({ ...row, [f.key + '_en']: v })}
        multiline={f.kind === 'textarea'}
      />
    )
  }
  if (f.kind === 'bool') {
    return <div>{renderFieldInput(f, row, setRow)}</div>
  }
  return (
    <Field label={f.label}>
      {renderFieldInput(f, row, setRow)}
    </Field>
  )
}

function EntityForm({
  schema, row, onSave, onCancel,
}: { schema: EntitySchema; row: Row; onSave: (r: Row) => Promise<void>; onCancel: () => void }) {
  const [local, setLocal] = useState<Row>(row)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setError(null); setBusy(true)
    try { await onSave(local) }
    catch (err) { setError(String(err)) }
    finally { setBusy(false) }
  }

  return (
    <div style={{ ...ui.card, marginTop: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {schema.fields.map((f) => (
          <FieldRow key={f.key} f={f} row={local} setRow={setLocal} />
        ))}
      </div>
      {error && <div style={{ color: '#a33', marginTop: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <button type="button" style={{ ...ui.btn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={save}>
          {busy ? '…' : 'Salva'}
        </button>
        <button type="button" style={ui.btnGhost} onClick={onCancel}>Annulla</button>
      </div>
    </div>
  )
}

export function EntityEditor({ entity }: { entity: keyof typeof schemas }) {
  const schema = schemas[entity]
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    try {
      const r = await apiJson<{ rows: Row[] }>(`/api/admin/entities/${entity}`)
      setRows(r.rows)
    } catch (err) {
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [entity])

  async function createRow(r: Row) {
    await apiJson(`/api/admin/entities/${entity}`, { method: 'POST', body: JSON.stringify(r) })
    setEditingId(null); await load()
  }

  async function updateRow(id: number, r: Row) {
    await apiJson(`/api/admin/entities/${entity}/${id}`, { method: 'PUT', body: JSON.stringify(r) })
    setEditingId(null); await load()
  }

  async function deleteRow(id: number) {
    if (!confirm('Eliminare?')) return
    await apiJson(`/api/admin/entities/${entity}/${id}`, { method: 'DELETE' })
    await load()
  }

  async function move(idx: number, delta: number) {
    const next = [...rows]
    const t = next[idx + delta]
    if (!t) return
    next[idx + delta] = next[idx]
    next[idx] = t
    setRows(next)
    const ids = next.map((r) => r.id as number)
    await apiJson(`/api/admin/entities/${entity}/reorder`, { method: 'POST', body: JSON.stringify({ order: ids }) })
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <h1 style={ui.sectionTitle}>{schema.label}</h1>
        <button type="button" style={ui.btn} onClick={() => setEditingId('new')}>+ Nuovo</button>
      </div>

      {editingId === 'new' && (
        <EntityForm
          schema={schema}
          row={schema.defaultRow}
          onSave={(r) => createRow(r)}
          onCancel={() => setEditingId(null)}
        />
      )}

      {error && <div style={{ color: '#a33', marginTop: 12 }}>{error}</div>}
      {loading && <div style={{ color: 'var(--fg-muted)', marginTop: 12 }}>Carico…</div>}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, idx) => {
          const id = r.id as number
          const canReorder = schema.fields.some((f) => f.key === 'sort_order')
          return (
            <div key={id}>
              <div style={{
                ...ui.card, padding: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 14 }}>
                    {schema.summary(r)}
                  </div>
                  {r.published === 0 && (
                    <div style={{ fontSize: 11, color: '#a33', letterSpacing: '0.2em', textTransform: 'uppercase', marginTop: 4 }}>
                      Bozza / nascosto
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {canReorder && (
                    <>
                      <button type="button" style={ui.btnGhost} onClick={() => move(idx, -1)} disabled={idx === 0}>↑</button>
                      <button type="button" style={ui.btnGhost} onClick={() => move(idx, +1)} disabled={idx === rows.length - 1}>↓</button>
                    </>
                  )}
                  <button type="button" style={ui.btnGhost} onClick={() => setEditingId(id)}>Modifica</button>
                  <button type="button" style={ui.btnDanger} onClick={() => deleteRow(id)}>Elimina</button>
                </div>
              </div>

              {editingId === id && (
                <EntityForm
                  schema={schema}
                  row={r}
                  onSave={(next) => updateRow(id, next)}
                  onCancel={() => setEditingId(null)}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
